import { Component } from "react";
import { connect } from "react-redux";

import { fleshOutConfiguredAttributes, type IConfiguredAttribute } from "../../../app-utils/playbook.js";
import { extractNumberFromString } from "../../../app-utils/string.js";
import {
  type Attribute,
  type ParentType,
  type ParentTypeSet,
  type Playbook,
  type RawAttributeDefault,
  type SampleType,
  type Workflow,
} from "../../../graphql.js";
import {
  type IPlaybookConfigState,
  savePlaybook,
  updatePlaybookConfigState,
} from "../../../redux/modules/playbook-admin.js";
import {
  convertWorkflowParamToTrackerAttribute,
  isNgsDataFileAttributeByUsage,
} from "../../../redux/modules/playbook-execution.js";
import { fetchFullSampleTypeWithAttributeDefaults } from "../../../redux/modules/sample-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import AttributesSelector from "./AttributesSelector.js";

type IPlaybookConfigExperimentInfoScreenOwnProps = IPlaybookWizardStepProvidedProps;

interface IPlaybookConfigExperimentInfoScreenAppStateProps {
  isLoadingSampleType: boolean;
  selectedSampleTypeId: number;
  selectedWorkflow: Workflow;
  selectedParentAttributes: Partial<IConfiguredAttribute>[];
  selectedWorkflowAttributes: Partial<IConfiguredAttribute>[];
  selectedPlaybookType: PLAYBOOK_TYPE;
  selectedSampleType: SampleType;
}

interface IPlaybookConfigExperimentInfoScreenDispatchProps {
  updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => void;
  fetchFullSampleTypeWithAttributeDefaults: (
    sampleTypeId: number,
  ) => Promise<{ sampleType: SampleType; attributeDefaults: RawAttributeDefault[] }>;
  savePlaybook: () => Promise<Playbook>;
}

interface IPlaybookConfigExperimentInfoScreenProps
  extends IPlaybookConfigExperimentInfoScreenAppStateProps,
    IPlaybookConfigExperimentInfoScreenDispatchProps,
    IPlaybookConfigExperimentInfoScreenOwnProps,
    IPlaybookWizardStepInjectedProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: IPlaybookConfigExperimentInfoScreenOwnProps,
): IPlaybookConfigExperimentInfoScreenAppStateProps {
  const selectedWorkflow: Workflow = state.listing.approvedWorkflows.find(
    (workflow) => workflow.sourceId === state.playbookAdminState.playbookConfigState.workflowId,
  );

  return {
    selectedSampleTypeId: state.playbookAdminState.playbookConfigState.sampleTypeId,
    selectedWorkflow: selectedWorkflow,
    selectedParentAttributes: state.playbookAdminState.playbookConfigState.parentAttributes,
    selectedWorkflowAttributes: state.playbookAdminState.playbookConfigState.workflowAttributes,
    isLoadingSampleType: state.sampleAdmin.isLoadingSampleTypeWithAttributeDefaults,
    selectedPlaybookType: state.playbookAdminState.playbookConfigState.playbookType,
    selectedSampleType: state.sampleAdmin.sampleType,
  };
}

function mapDispatchToProps(dispatch): IPlaybookConfigExperimentInfoScreenDispatchProps {
  return {
    updatePlaybookCreationState: (state: Partial<IPlaybookConfigState>) => dispatch(updatePlaybookConfigState(state)),
    fetchFullSampleTypeWithAttributeDefaults: (sampleTypeId: number) =>
      dispatch(fetchFullSampleTypeWithAttributeDefaults(sampleTypeId)),
    savePlaybook: () => dispatch(savePlaybook()),
  };
}

interface IPlaybookConfigExperimentInfoScreenState {
  parentAttributes: Attribute[];
  selectedParentAttributes: IConfiguredAttribute[];
  attributeDefaults: RawAttributeDefault[];
  workflowFields: Attribute[];
  selectedWorkflowFields: IConfiguredAttribute[];
  isSavingPlaybook: boolean;
  formError: string;
}

class PlaybookConfigExperimentInfoScreenImpl extends Component<
  IPlaybookConfigExperimentInfoScreenProps,
  IPlaybookConfigExperimentInfoScreenState
> {
  constructor(props) {
    super(props);

    this.state = {
      parentAttributes: [],
      selectedParentAttributes: [],
      attributeDefaults: null,
      workflowFields: [],
      selectedWorkflowFields: [],
      isSavingPlaybook: false,
      formError: null,
    };
  }

  loadData = () => {
    // load the sample type only if we need the related attributes, otherwise only process the workflow params
    if (
      this.props.selectedPlaybookType !== PLAYBOOK_TYPE.NGS_ANALYSIS &&
      this.props.selectedPlaybookType !== PLAYBOOK_TYPE.RANGE_ANALYSIS
    ) {
      this.props
        .fetchFullSampleTypeWithAttributeDefaults(this.props.selectedSampleTypeId)
        .then((result: { sampleType: SampleType; attributeDefaults: RawAttributeDefault[] }) => {
          const sampleType: SampleType = result.sampleType;
          // only consider the primary parent type attributes, attributes of non-primary parents should never appear in the registration UI
          const parentAttributes: Attribute[] = sampleType.parentTypeSets.find((p: ParentTypeSet) => p.isPrimary)
            .parentType.parentAttributes;

          this.setState({
            attributeDefaults: result.attributeDefaults,
          });

          this.setParentAttributesList(parentAttributes);
        })
        .catch((error: Error) => {
          this.props.updateWizardMessage(error.message);
          throw error;
        });
    }

    if (this.props.selectedPlaybookType !== PLAYBOOK_TYPE.SAMPLE_REGISTRATION) {
      this.setWorkflowAttributesList();
    }
  };

  static initAttributes = (
    attributes: Attribute[],
    selectedAttributes: Partial<IConfiguredAttribute>[],
  ): IConfiguredAttribute[] => {
    // flesh out the selected attributes with full data (since we store only basic information)
    const fleshedOutSelectedAttributes: IConfiguredAttribute[] = fleshOutConfiguredAttributes(
      selectedAttributes,
      attributes,
    );

    // build the initial selection, and add new mandatory elements (if necessary) to the current selection
    const missingMandatoryAttributes = attributes
      .filter((attribute: Attribute) => attribute.mandatory)
      .filter(
        (mandatoryAttribute: Attribute) =>
          !fleshedOutSelectedAttributes.find(
            (selectedAttribute: IConfiguredAttribute) => selectedAttribute.id === mandatoryAttribute.id,
          ),
      );

    fleshedOutSelectedAttributes.push(...missingMandatoryAttributes);
    return fleshedOutSelectedAttributes;
  };

  setParentAttributesList = (parentAttributes: Attribute[]) => {
    const shownParentAttributes = parentAttributes.filter((a: Attribute) => !a.automatic); // automatic attributes are never shown in the playbook forms (SEL6-3376)
    const selectedParentAttributes = PlaybookConfigExperimentInfoScreenImpl.initAttributes(
      shownParentAttributes,
      this.props.selectedParentAttributes,
    );

    this.setState({
      parentAttributes: shownParentAttributes,
      selectedParentAttributes: selectedParentAttributes,
    });
  };

  setWorkflowAttributesList = () => {
    const workflowAttributes: Attribute[] =
      this.props.selectedWorkflow?.workflowParams
        // sort by activityIndex (natural ordering of the workflow)
        .sort(
          (workflowParam1, workflowParam2) =>
            extractNumberFromString(workflowParam1.id) - extractNumberFromString(workflowParam2.id),
        )
        .map(convertWorkflowParamToTrackerAttribute)
        .filter((a: Attribute) => !isNgsDataFileAttributeByUsage(a)) || []; // raw data files will be part of sample registration form

    const selectedWorkflowAttributes = PlaybookConfigExperimentInfoScreenImpl.initAttributes(
      workflowAttributes,
      this.props.selectedWorkflowAttributes,
    );

    this.setState({
      workflowFields: workflowAttributes,
      selectedWorkflowFields: selectedWorkflowAttributes,
    });
  };

  componentDidMount() {
    this.loadData();
  }

  handleSubmitData = () => {
    // at this stage the validation has succeeded

    const playbookCreationState: Partial<IPlaybookConfigState> = {
      parentAttributes: this.state.selectedParentAttributes,
      workflowAttributes: this.state.selectedWorkflowFields,
    };

    this.props.updatePlaybookCreationState(playbookCreationState);

    if (
      this.props.selectedPlaybookType === PLAYBOOK_TYPE.NGS_ANALYSIS ||
      this.props.selectedPlaybookType === PLAYBOOK_TYPE.RANGE_ANALYSIS
    ) {
      this.setState({ formError: null, isSavingPlaybook: true });
      return this.props
        .savePlaybook()
        .then(() => {
          this.setState({ isSavingPlaybook: false });
          return this.props.proceedWithNextStep();
        })
        .catch((error: Error) => {
          this.setState({ formError: error.message, isSavingPlaybook: false });
        });
    } else {
      return this.props.proceedWithNextStep();
    }
  };

  parentAttributesUpdated = (selectedParentAttributes: IConfiguredAttribute[]) => {
    this.setState({ selectedParentAttributes: selectedParentAttributes });
  };

  workflowAttributesUpdated = (selectedWorkflowAttributes: IConfiguredAttribute[]) => {
    this.setState({ selectedWorkflowFields: selectedWorkflowAttributes });
  };

  getPrimaryParentName = () => {
    const primaryParentType: ParentType = this.props.selectedSampleType?.parentTypeSets.find(
      (parentTypeSet) => parentTypeSet.isPrimary,
    )?.parentType;

    if (primaryParentType) {
      return `'${primaryParentType?.name}'`;
    }

    return "Sample Parent";
  };

  pageDescription = () => {
    const parentName: string = this.getPrimaryParentName();
    switch (this.props.selectedPlaybookType) {
      case PLAYBOOK_TYPE.SAMPLE_REGISTRATION: {
        return `You can configure the Playbook so that the user provides information
for the ${parentName} that is associated with these samples. Select the ${parentName}
 attributes that the users will provide during Playbook execution.`;
      }
      case PLAYBOOK_TYPE.NGS_ANALYSIS:
      case PLAYBOOK_TYPE.RANGE_ANALYSIS: {
        return `Select the workflow parameters that the users will provide during Playbook execution.`;
      }
      case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES: {
        return `You can configure the Playbook so that the user provides information
for the ${parentName} that is associated with these samples. Select the ${parentName}
 attributes and workflow parameters that the users will provide during Playbook execution.`;
      }
    }
  };

  render() {
    const isLastScreen: boolean =
      this.props.selectedPlaybookType === PLAYBOOK_TYPE.NGS_ANALYSIS ||
      this.props.selectedPlaybookType === PLAYBOOK_TYPE.RANGE_ANALYSIS;
    return (
      <PlaybookWizardStep
        {...this.props}
        playbookPage={{
          ...this.props.playbookPage,
          description: this.pageDescription(),
        }}
        wizardFooterData={{
          error: this.state.formError,
          nextButtonProps: {
            label: isLastScreen ? "Save Playbook" : "Next",
            isDisabled: isLastScreen ? false : this.props.isLoadingSampleType,
            isLoading: isLastScreen ? this.state.isSavingPlaybook : false,
            onPress: this.handleSubmitData,
          },
        }}
      >
        {!!this.state.parentAttributes?.length && (
          <div className="experiment-info-screen">
            <div className="form-group">
              <label className="control-label">
                {`${
                  this.state.selectedParentAttributes.length > 0 ? this.state.selectedParentAttributes.length : "No "
                } ${this.getPrimaryParentName()} attribute(s) selected`}
              </label>
              <AttributesSelector
                isLoadingAttributes={this.props.isLoadingSampleType}
                selectedAttributes={this.state.selectedParentAttributes}
                availableAttributes={this.state.parentAttributes}
                attributesUpdated={this.parentAttributesUpdated}
                dropdownLabel={
                  this.state.parentAttributes.length === this.state.selectedParentAttributes.length
                    ? "There are no additional attributes to select"
                    : "Add attributes"
                }
              />
            </div>
          </div>
        )}

        {!!this.state.workflowFields?.length && (
          <div className="experiment-info-screen">
            <div className="form-group">
              <label className="control-label">
                {`${
                  this.state.selectedWorkflowFields.length > 0 ? this.state.selectedWorkflowFields.length : "No "
                } workflow parameter(s) selected`}
              </label>
              <AttributesSelector
                isLoadingAttributes={this.props.isLoadingSampleType}
                selectedAttributes={this.state.selectedWorkflowFields}
                availableAttributes={this.state.workflowFields}
                // attributeDefaults={this.state.attributeDefaults}
                attributesUpdated={this.workflowAttributesUpdated}
                dropdownLabel={
                  this.state.workflowFields.length === this.state.selectedWorkflowFields.length
                    ? "There are no additional workflow parameters to select"
                    : "Select additional workflow parameters"
                }
                disableReordering={true}
              />
            </div>
          </div>
        )}
      </PlaybookWizardStep>
    );
  }
}

export default connect<
  IPlaybookConfigExperimentInfoScreenAppStateProps,
  IPlaybookConfigExperimentInfoScreenDispatchProps,
  IPlaybookConfigExperimentInfoScreenOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(PlaybookConfigExperimentInfoScreenImpl) as any;
